import {
  CREATOR_SITE_SECTIONS_MAX,
  type CreatorSiteSectionType,
  isCreatorSiteSectionType,
  validateCreatorSitePageSlug,
} from './creator-site';
import { CREATOR_SITE_INQUIRY_TYPES_MAX } from './creator-site-leads';
import { validateCreatorSiteSectionVisibility } from './creator-site-visibility';
import { validateCreatorSiteEmbedContent } from './creator-site-embed';
import { validateCreatorSiteSectionVariant } from './creator-site-variants';

export const CREATOR_SITE_SITE_TITLE_MAX = 120;
export const CREATOR_SITE_SEO_TITLE_MAX = 70;
export const CREATOR_SITE_SEO_DESCRIPTION_MAX = 320;
export const CREATOR_SITE_SECTION_VARIANT_MAX = 64;
export const CREATOR_SITE_TEXT_SHORT_MAX = 120;
export const CREATOR_SITE_TEXT_MEDIUM_MAX = 240;
export const CREATOR_SITE_TEXT_LONG_MAX = 10000;
export const CREATOR_SITE_RICH_TEXT_MAX = 20000;
export const CREATOR_SITE_LINKS_MAX = 20;
export const CREATOR_SITE_FAQ_ITEMS_MAX = 20;
export const CREATOR_SITE_TESTIMONIALS_MAX = 12;
export const CREATOR_SITE_CASE_STUDIES_MAX = 6;
export const CREATOR_SITE_BINDING_IDS_MAX = 12;
export const CREATOR_SITE_ARTICLE_LIST_LIMIT_MAX = 12;

export type CreatorSiteSectionBindings = {
  offerId?: string;
  offerIds?: string[];
  courseIds?: string[];
  membershipId?: string;
  articleId?: string;
  articleIds?: string[];
  publicationId?: string;
  limit?: number;
  tag?: string;
};

export type CreatorSiteSectionInput = {
  id?: string;
  type: string;
  variant?: string;
  visible?: boolean;
  visibility?: unknown;
  content: unknown;
  bindings?: unknown;
};

type ValidationResult = { ok: true } | { ok: false; message: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}

function isOptionalString(value: unknown, max: number): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.length <= max);
}

function validateLinkItems(
  value: unknown,
  options: { required?: boolean; maxItems: number },
): ValidationResult {
  if (value === undefined) {
    return options.required ? { ok: false, message: 'links are required' } : { ok: true };
  }
  if (!Array.isArray(value)) {
    return { ok: false, message: 'links must be an array' };
  }
  if (options.required && value.length === 0) {
    return { ok: false, message: 'links must include at least one item' };
  }
  if (value.length > options.maxItems) {
    return { ok: false, message: `links cannot exceed ${options.maxItems} items` };
  }

  for (const item of value) {
    if (!isPlainObject(item)) {
      return { ok: false, message: 'each link must be an object' };
    }
    if (!isNonEmptyString(item.label, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each link requires a label' };
    }
    if (!isNonEmptyString(item.href, 500)) {
      return { ok: false, message: 'each link requires an href' };
    }
  }

  return { ok: true };
}

function validateHeroContent(content: Record<string, unknown>): ValidationResult {
  if (!isNonEmptyString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'hero headline is required' };
  }
  if (!isOptionalString(content.subheadline, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
    return { ok: false, message: 'hero subheadline is too long' };
  }
  if (!isOptionalString(content.ctaLabel, 40)) {
    return { ok: false, message: 'hero ctaLabel is too long' };
  }
  if (!isOptionalString(content.ctaHref, 500)) {
    return { ok: false, message: 'hero ctaHref is too long' };
  }
  if (!isOptionalString(content.imageAssetId, 64)) {
    return { ok: false, message: 'hero imageAssetId is invalid' };
  }
  if (!isOptionalString(content.videoAssetId, 64)) {
    return { ok: false, message: 'hero videoAssetId is invalid' };
  }
  if (!isOptionalString(content.imageUrl, 500)) {
    return { ok: false, message: 'hero imageUrl is too long' };
  }
  if (!isOptionalString(content.backgroundVideoUrl, 500)) {
    return { ok: false, message: 'hero backgroundVideoUrl is too long' };
  }
  return { ok: true };
}

function validateAboutContent(content: Record<string, unknown>): ValidationResult {
  if (!isNonEmptyString(content.body, CREATOR_SITE_TEXT_LONG_MAX)) {
    return { ok: false, message: 'about body is required' };
  }
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'about headline is too long' };
  }
  if (!isOptionalString(content.imageAssetId, 64)) {
    return { ok: false, message: 'about imageAssetId is invalid' };
  }
  return { ok: true };
}

function validateRichTextContent(content: Record<string, unknown>): ValidationResult {
  if (!isNonEmptyString(content.body, CREATOR_SITE_RICH_TEXT_MAX)) {
    return { ok: false, message: 'rich_text body is required' };
  }
  return { ok: true };
}

function validateFaqContent(content: Record<string, unknown>): ValidationResult {
  const items = content.items;
  if (!Array.isArray(items) || items.length === 0) {
    return { ok: false, message: 'faq items are required' };
  }
  if (items.length > CREATOR_SITE_FAQ_ITEMS_MAX) {
    return { ok: false, message: `faq cannot exceed ${CREATOR_SITE_FAQ_ITEMS_MAX} items` };
  }
  for (const item of items) {
    if (!isPlainObject(item)) {
      return { ok: false, message: 'each faq item must be an object' };
    }
    if (!isNonEmptyString(item.question, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
      return { ok: false, message: 'each faq item requires a question' };
    }
    if (!isNonEmptyString(item.answer, CREATOR_SITE_TEXT_LONG_MAX)) {
      return { ok: false, message: 'each faq item requires an answer' };
    }
  }
  return { ok: true };
}

function validateTestimonialsContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'testimonials headline is too long' };
  }
  const items = content.items;
  if (!Array.isArray(items) || items.length === 0) {
    return { ok: false, message: 'testimonials items are required' };
  }
  if (items.length > CREATOR_SITE_TESTIMONIALS_MAX) {
    return {
      ok: false,
      message: `testimonials cannot exceed ${CREATOR_SITE_TESTIMONIALS_MAX} items`,
    };
  }
  for (const item of items) {
    if (!isPlainObject(item)) {
      return { ok: false, message: 'each testimonial must be an object' };
    }
    if (!isNonEmptyString(item.quote, CREATOR_SITE_TEXT_LONG_MAX)) {
      return { ok: false, message: 'each testimonial requires a quote' };
    }
    if (!isNonEmptyString(item.author, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each testimonial requires an author' };
    }
    if (!isNonEmptyString(item.source, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
      return { ok: false, message: 'each testimonial requires a source' };
    }
    if (!isOptionalString(item.role, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'testimonial role is too long' };
    }
  }
  return { ok: true };
}

function validateCaseStudyContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'case_study headline is too long' };
  }
  const items = content.items;
  if (!Array.isArray(items) || items.length === 0) {
    return { ok: false, message: 'case_study items are required' };
  }
  if (items.length > CREATOR_SITE_CASE_STUDIES_MAX) {
    return {
      ok: false,
      message: `case_study cannot exceed ${CREATOR_SITE_CASE_STUDIES_MAX} items`,
    };
  }
  for (const item of items) {
    if (!isPlainObject(item)) {
      return { ok: false, message: 'each case study must be an object' };
    }
    if (!isNonEmptyString(item.title, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each case study requires a title' };
    }
    if (!isNonEmptyString(item.outcome, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
      return { ok: false, message: 'each case study requires an outcome' };
    }
    if (!isOptionalString(item.summary, CREATOR_SITE_TEXT_LONG_MAX)) {
      return { ok: false, message: 'case study summary is too long' };
    }
    if (!isOptionalString(item.imageUrl, 500)) {
      return { ok: false, message: 'case study imageUrl is too long' };
    }
    if (item.imageUrl && typeof item.imageUrl === 'string' && item.imageUrl.trim()) {
      const url = item.imageUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return { ok: false, message: 'case study imageUrl must be an http(s) URL' };
      }
    }
    if (!isOptionalString(item.ctaLabel, 40)) {
      return { ok: false, message: 'case study ctaLabel is too long' };
    }
    if (item.pageSlug !== undefined && item.pageSlug !== null && item.pageSlug !== '') {
      if (typeof item.pageSlug !== 'string') {
        return { ok: false, message: 'case study pageSlug must be a string' };
      }
      const slugResult = validateCreatorSitePageSlug(item.pageSlug);
      if (!slugResult.ok) {
        return { ok: false, message: slugResult.message };
      }
    }
  }
  return { ok: true };
}

function validateContactFormContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'contact_form headline is too long' };
  }
  if (!isOptionalString(content.buttonLabel, 40)) {
    return { ok: false, message: 'contact_form buttonLabel is too long' };
  }
  if (!isOptionalString(content.consentText, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
    return { ok: false, message: 'contact_form consentText is too long' };
  }
  const inquiryTypes = content.inquiryTypes;
  if (inquiryTypes === undefined || inquiryTypes === null) {
    return { ok: true };
  }
  if (!Array.isArray(inquiryTypes)) {
    return { ok: false, message: 'contact_form inquiryTypes must be an array' };
  }
  if (inquiryTypes.length > CREATOR_SITE_INQUIRY_TYPES_MAX) {
    return {
      ok: false,
      message: `contact_form inquiryTypes cannot exceed ${CREATOR_SITE_INQUIRY_TYPES_MAX} items`,
    };
  }
  for (const type of inquiryTypes) {
    if (!isNonEmptyString(type, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each inquiry type must be a non-empty string' };
    }
  }
  return { ok: true };
}

function validateNewsletterContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'newsletter headline is too long' };
  }
  if (!isOptionalString(content.buttonLabel, 40)) {
    return { ok: false, message: 'newsletter buttonLabel is too long' };
  }
  if (!isOptionalString(content.consentText, CREATOR_SITE_TEXT_MEDIUM_MAX)) {
    return { ok: false, message: 'newsletter consentText is too long' };
  }
  return { ok: true };
}

function validateFollowLoamifyContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'follow_loamify headline is too long' };
  }
  if (!isOptionalString(content.buttonLabel, 40)) {
    return { ok: false, message: 'follow_loamify buttonLabel is too long' };
  }
  return { ok: true };
}

function validateNavbarContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.logoAssetId, 64)) {
    return { ok: false, message: 'navbar logoAssetId is invalid' };
  }
  return validateLinkItems(content.links, { maxItems: 10 });
}

function validateEmbedContent(content: Record<string, unknown>): ValidationResult {
  const result = validateCreatorSiteEmbedContent(content);
  return result.ok ? { ok: true } : { ok: false, message: result.message };
}

function validateFooterContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.copyrightText, 200)) {
    return { ok: false, message: 'footer copyrightText is too long' };
  }
  return validateLinkItems(content.links, { maxItems: 10 });
}

function validateMediaItems(
  value: unknown,
  options: { field: string; required?: boolean; maxItems: number },
): ValidationResult {
  if (value === undefined) {
    return options.required ? { ok: false, message: `${options.field} are required` } : { ok: true };
  }
  if (!Array.isArray(value)) {
    return { ok: false, message: `${options.field} must be an array` };
  }
  if (options.required && value.length === 0) {
    return { ok: false, message: `${options.field} must include at least one item` };
  }
  if (value.length > options.maxItems) {
    return { ok: false, message: `${options.field} cannot exceed ${options.maxItems} items` };
  }
  for (const item of value) {
    if (!isPlainObject(item)) {
      return { ok: false, message: `each ${options.field} item must be an object` };
    }
    if (!isNonEmptyString(item.imageUrl, 500) && !isNonEmptyString(item.url, 500)) {
      return { ok: false, message: `each ${options.field} item requires imageUrl or url` };
    }
    if (!isOptionalString(item.alt, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: `${options.field} alt is too long` };
    }
    if (!isOptionalString(item.name, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: `${options.field} name is too long` };
    }
    if (!isOptionalString(item.href, 500)) {
      return { ok: false, message: `${options.field} href is too long` };
    }
  }
  return { ok: true };
}

function validateStatsContent(content: Record<string, unknown>): ValidationResult {
  if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
    return { ok: false, message: 'stats headline is too long' };
  }
  const items = content.items;
  if (!Array.isArray(items) || items.length === 0) {
    return { ok: false, message: 'stats items are required' };
  }
  if (items.length > 6) {
    return { ok: false, message: 'stats cannot exceed 6 items' };
  }
  for (const item of items) {
    if (!isPlainObject(item)) {
      return { ok: false, message: 'each stats item must be an object' };
    }
    if (!isNonEmptyString(item.label, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each stats item requires a label' };
    }
    if (!isNonEmptyString(item.value, CREATOR_SITE_TEXT_SHORT_MAX)) {
      return { ok: false, message: 'each stats item requires a value' };
    }
  }
  return { ok: true };
}

function validateSpacerContent(content: Record<string, unknown>): ValidationResult {
  const size = content.size;
  if (size === undefined || size === null) return { ok: true };
  if (size !== 'sm' && size !== 'md' && size !== 'lg') {
    return { ok: false, message: 'spacer size must be sm, md, or lg' };
  }
  return { ok: true };
}

export function validateCreatorSiteHeroPrimaryCta(content: unknown): ValidationResult {
  if (!isPlainObject(content)) {
    return { ok: false, message: 'hero content is required' };
  }
  if (!isNonEmptyString(content.ctaLabel, 40)) {
    return { ok: false, message: 'hero primary CTA label is required to publish' };
  }
  if (!isNonEmptyString(content.ctaHref, 500)) {
    return { ok: false, message: 'hero primary CTA link is required to publish' };
  }
  return { ok: true };
}

export function validateCreatorSiteSectionContent(
  type: CreatorSiteSectionType,
  content: unknown,
): ValidationResult {
  if (!isPlainObject(content)) {
    return { ok: false, message: 'section content must be an object' };
  }

  switch (type) {
    case 'hero':
      return validateHeroContent(content);
    case 'about':
      return validateAboutContent(content);
    case 'rich_text':
      return validateRichTextContent(content);
    case 'faq':
      return validateFaqContent(content);
    case 'testimonials':
      return validateTestimonialsContent(content);
    case 'case_study':
      return validateCaseStudyContent(content);
    case 'link_row':
      return validateLinkItems(content.links, { required: true, maxItems: CREATOR_SITE_LINKS_MAX });
    case 'navbar':
      return validateNavbarContent(content);
    case 'footer':
      return validateFooterContent(content);
    case 'newsletter_signup':
      return validateNewsletterContent(content);
    case 'contact_form':
      return validateContactFormContent(content);
    case 'embed':
      return validateEmbedContent(content);
    case 'image_gallery':
      if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
        return { ok: false, message: 'image_gallery headline is too long' };
      }
      return validateMediaItems(content.images, { field: 'images', required: true, maxItems: 12 });
    case 'logo_strip':
      if (!isOptionalString(content.headline, CREATOR_SITE_TEXT_SHORT_MAX)) {
        return { ok: false, message: 'logo_strip headline is too long' };
      }
      return validateMediaItems(content.logos, { field: 'logos', required: true, maxItems: 12 });
    case 'stats':
      return validateStatsContent(content);
    case 'spacer':
      return validateSpacerContent(content);
    case 'follow_loamify':
      return validateFollowLoamifyContent(content);
    case 'social_links':
    case 'featured_offer':
    case 'offer_grid':
    case 'course_showcase':
    case 'membership_card':
    case 'event_list':
    case 'article_list':
    case 'featured_article':
      return { ok: true };
    default:
      return { ok: false, message: 'Invalid section type' };
  }
}

function validateIdList(
  value: unknown,
  options: { field: string; required?: boolean; max?: number },
): ValidationResult {
  if (value === undefined || value === null) {
    return options.required ? { ok: false, message: `${options.field} is required` } : { ok: true };
  }
  if (!Array.isArray(value)) {
    return { ok: false, message: `${options.field} must be an array` };
  }
  if (options.required && value.length === 0) {
    return { ok: false, message: `${options.field} must include at least one id` };
  }
  const max = options.max ?? CREATOR_SITE_BINDING_IDS_MAX;
  if (value.length > max) {
    return { ok: false, message: `${options.field} cannot exceed ${max} items` };
  }
  for (const id of value) {
    if (typeof id !== 'string' || !id.trim()) {
      return { ok: false, message: `${options.field} must contain string ids` };
    }
  }
  return { ok: true };
}

function validateSingleId(value: unknown, field: string, required = false): ValidationResult {
  if (value === undefined || value === null) {
    return required ? { ok: false, message: `${field} is required` } : { ok: true };
  }
  if (typeof value !== 'string' || !value.trim()) {
    return { ok: false, message: `${field} must be a string` };
  }
  return { ok: true };
}

export function validateCreatorSiteSectionBindings(
  type: CreatorSiteSectionType,
  bindings: unknown,
): ValidationResult {
  if (bindings === undefined || bindings === null) {
    bindings = {};
  }
  if (!isPlainObject(bindings)) {
    return { ok: false, message: 'section bindings must be an object' };
  }

  switch (type) {
    case 'featured_offer':
      return validateSingleId(bindings.offerId, 'offerId', true);
    case 'offer_grid':
      return validateIdList(bindings.offerIds, { field: 'offerIds', required: true });
    case 'course_showcase':
      return validateIdList(bindings.courseIds, { field: 'courseIds', required: true });
    case 'membership_card':
      return validateSingleId(bindings.membershipId, 'membershipId', false);
    case 'featured_article':
      return validateSingleId(bindings.articleId, 'articleId', true);
    case 'article_list': {
      const idsResult = validateIdList(bindings.articleIds, { field: 'articleIds' });
      if (!idsResult.ok) return idsResult;
      if (bindings.limit !== undefined && bindings.limit !== null) {
        if (
          typeof bindings.limit !== 'number' ||
          !Number.isInteger(bindings.limit) ||
          bindings.limit < 1 ||
          bindings.limit > CREATOR_SITE_ARTICLE_LIST_LIMIT_MAX
        ) {
          return { ok: false, message: 'article_list limit must be between 1 and 12' };
        }
      }
      if (bindings.tag !== undefined && bindings.tag !== null) {
        if (typeof bindings.tag !== 'string' || !bindings.tag.trim()) {
          return { ok: false, message: 'article_list tag must be a non-empty string' };
        }
        if (bindings.tag.length > CREATOR_SITE_TEXT_SHORT_MAX) {
          return { ok: false, message: 'article_list tag is too long' };
        }
      }
      return { ok: true };
    }
    case 'newsletter_signup': {
      const publicationResult = validateSingleId(bindings.publicationId, 'publicationId');
      if (!publicationResult.ok) return publicationResult;
      const extraKeys = Object.keys(bindings).filter((key) => key !== 'publicationId');
      if (extraKeys.length > 0) {
        return { ok: false, message: 'newsletter_signup bindings only support publicationId' };
      }
      return { ok: true };
    }
    default:
      if (Object.keys(bindings).length > 0) {
        return { ok: false, message: `${type} sections do not support bindings` };
      }
      return { ok: true };
  }
}

export function validateCreatorSiteSectionInput(
  section: CreatorSiteSectionInput,
): ValidationResult {
  if (!isCreatorSiteSectionType(section.type)) {
    return { ok: false, message: 'Invalid section type' };
  }
  if (
    section.variant !== undefined &&
    (typeof section.variant !== 'string' || section.variant.length > CREATOR_SITE_SECTION_VARIANT_MAX)
  ) {
    return { ok: false, message: 'section variant is invalid' };
  }
  const variantResult = validateCreatorSiteSectionVariant(
    section.type,
    section.variant ?? 'default',
  );
  if (!variantResult.ok) return variantResult;
  if (section.visible !== undefined && typeof section.visible !== 'boolean') {
    return { ok: false, message: 'section visible must be a boolean' };
  }

  const visibilityResult = validateCreatorSiteSectionVisibility(section.visibility);
  if (!visibilityResult.ok) return visibilityResult;

  const contentResult = validateCreatorSiteSectionContent(section.type, section.content);
  if (!contentResult.ok) return contentResult;

  return validateCreatorSiteSectionBindings(section.type, section.bindings);
}

export function validateCreatorSiteSectionsPatch(
  sections: CreatorSiteSectionInput[],
): ValidationResult {
  if (!Array.isArray(sections)) {
    return { ok: false, message: 'sections must be an array' };
  }
  if (sections.length > CREATOR_SITE_SECTIONS_MAX) {
    return { ok: false, message: `sections cannot exceed ${CREATOR_SITE_SECTIONS_MAX}` };
  }

  const seenIds = new Set<string>();
  for (const section of sections) {
    if (section.id) {
      if (seenIds.has(section.id)) {
        return { ok: false, message: 'duplicate section id in payload' };
      }
      seenIds.add(section.id);
    }
    const result = validateCreatorSiteSectionInput(section);
    if (!result.ok) return result;
  }

  return { ok: true };
}

export function normalizeCreatorSiteSectionBindings(
  type: CreatorSiteSectionType,
  bindings: unknown,
): CreatorSiteSectionBindings | null {
  if (!isPlainObject(bindings) || Object.keys(bindings).length === 0) {
    return null;
  }

  switch (type) {
    case 'featured_offer':
      return { offerId: String(bindings.offerId) };
    case 'offer_grid':
      return { offerIds: (bindings.offerIds as string[]).map(String) };
    case 'course_showcase':
      return { courseIds: (bindings.courseIds as string[]).map(String) };
    case 'membership_card':
      return bindings.membershipId ? { membershipId: String(bindings.membershipId) } : {};
    case 'featured_article':
      return { articleId: String(bindings.articleId) };
    case 'article_list':
      return {
        ...(bindings.articleIds ? { articleIds: (bindings.articleIds as string[]).map(String) } : {}),
        ...(bindings.limit !== undefined && bindings.limit !== null
          ? { limit: Number(bindings.limit) }
          : {}),
        ...(bindings.tag && String(bindings.tag).trim()
          ? { tag: String(bindings.tag).trim() }
          : {}),
      };
    case 'newsletter_signup':
      return bindings.publicationId
        ? { publicationId: String(bindings.publicationId).trim() }
        : {};
    default:
      return null;
  }
}
